import { nanoid } from 'nanoid/non-secure';
import FormData from 'form-data';
import axios from 'axios';
import { API_ENDPOINTS, AUTH_RESOURCE_TYPE, CLIENT_ID } from '../constants/index.esm.js';

/**
 * Retrieves an authentication token from the OCR API
 *
 * @param ocrBaseUrl - Base URL for the OCR API
 * @param clientSecretKey - Secret key for client authentication
 * @param tokenApiKey - API key for token endpoint
 * @param clientId - Optional client ID to override default CLIENT_ID
 * @param resourceType - Optional resource type to override default AUTH_RESOURCE_TYPE
 * @returns Promise resolving to TokenResponse or undefined if request fails
 */ const fetchToken = async (ocrBaseUrl, clientSecretKey, tokenApiKey, clientId, resourceType)=>{
    // Generate unique device ID for authentication
    const deviceId = nanoid();
    try {
        // Construct token endpoint URL
        const tokenUrl = `${ocrBaseUrl}${API_ENDPOINTS.token}`;
        // Create payload as URL encoded form data
        const payload = new URLSearchParams({
            client_id: clientId || CLIENT_ID,
            client_secret: clientSecretKey,
            resource_type: resourceType || AUTH_RESOURCE_TYPE,
            device_id: deviceId
        }).toString();
        // Send token request with proper headers
        const response = await axios.post(tokenUrl, payload, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded',
                apiKey: tokenApiKey
            },
            timeout: 10000
        });
        return response.data;
    } catch (error) {
        // Extract meaningful error message
        let errorMessage;
        if (axios.isAxiosError(error)) {
            var _error_response_data, _error_response;
            errorMessage = ((_error_response = error.response) == null ? void 0 : (_error_response_data = _error_response.data) == null ? void 0 : _error_response_data.message) || error.message;
        } else if (error instanceof Error) {
            errorMessage = error.message;
        } else {
            errorMessage = 'Unknown error';
        }
        console.warn('Error fetching token:', errorMessage);
        return undefined;
    }
};
/**
 * Extract document data using OCR with authentication
 * @param baseUrl - Base URL for the API
 * @param apiKey - API key for authentication
 * @param documentType - Type of document to extract
 * @param file - Single file or array of files to process
 * @param token - Authentication token
 * @param referenceId - Unique reference ID for the request
 * @param client - Client type (any string value, e.g., 'Mobile', 'Web', etc.)
 * @param abortController - Optional AbortController to cancel the request
 * @param num_pages - Optional number of pages to process from the document
 * @returns Promise resolving to extraction results
 */ const extractDocumentWithAuth = async (baseUrl, apiKey, documentType, file, token, referenceId, client, abortController, num_pages)=>{
    try {
        // Create and populate form data
        const formData = new FormData();
        formData.append('file', file);
        formData.append('ref_id', referenceId);
        formData.append('client', client);
        // Add num_pages to the form data if it's provided
        if (num_pages !== undefined) {
            formData.append('num_pages', num_pages.toString());
        }
        // Build endpoint URL with document type parameter
        const url = new URL(`${baseUrl}${API_ENDPOINTS.extractDocument}`);
        url.searchParams.append('document_type', decodeURIComponent(documentType));
        // Send API request with the AbortController signal
        // AbortController timeout is already set up in ocrShared.ts
        const response = await axios.post(url.toString(), formData, {
            headers: {
                Authorization: `Bearer ${token.trim()}`,
                apiKey,
                Accept: 'application/json',
                'Content-Type': 'multipart/form-data'
            },
            signal: abortController == null ? void 0 : abortController.signal // Use AbortController signal for timeout management
        });
        return {
            success: true,
            data: response.data
        };
    } catch (error) {
        // Enhanced error handling
        let errorMessage;
        let isAborted = false;
        if (axios.isAxiosError(error)) {
            // Check if it's an abort error
            if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
                isAborted = true;
                errorMessage = 'Request was aborted due to timeout or manual cancellation';
            } else {
                var _error_response_data, _error_response;
                errorMessage = ((_error_response = error.response) == null ? void 0 : (_error_response_data = _error_response.data) == null ? void 0 : _error_response_data.detail) || error.message;
            }
        } else if (error instanceof Error) {
            // Check if it's an abort error (DOMException or AbortError)
            if (error.name === 'AbortError' || error.message.includes('abort')) {
                isAborted = true;
                errorMessage = 'Request was aborted due to timeout or manual cancellation';
            } else {
                errorMessage = error.message;
            }
        } else {
            errorMessage = 'Unknown error';
        }
        console.warn('Document extraction failed:', errorMessage);
        return {
            success: false,
            error: errorMessage,
            aborted: isAborted
        };
    }
};

export { extractDocumentWithAuth, fetchToken };
